import React from 'react';
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  MapPin, 
  Truck, 
  TrendingUp,
  BarChart3,
  FileText,
  Settings,
  Menu,
  X
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  isCollapsed: boolean;
  onCollapseToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onToggle, 
  currentPath, 
  onNavigate, 
  isCollapsed, 
  onCollapseToggle 
}) => {

  const navigationSections = [
    {
      title: 'Overview',
      items: [
        {
          name: 'Dashboard',
          path: '/dashboard',
          icon: LayoutDashboard,
          description: 'Overview and analytics'
        }
      ]
    },
    {
      title: 'Inventory Management',
      items: [
        {
          name: 'Parts Inventory',
          path: '/parts',
          icon: Package,
          description: 'Manage vehicle parts'
        },
        {
          name: 'Categories',
          path: '/categories',
          icon: FolderOpen,
          description: 'Part categories'
        },
        {
          name: 'Locations',
          path: '/locations',
          icon: MapPin,
          description: 'Storage locations'
        }
      ]
    },
    {
      title: 'Operations',
      items: [
        {
          name: 'Suppliers',
          path: '/suppliers',
          icon: Truck,
          description: 'Vendor management'
        },
        {
          name: 'Stock Movements',
          path: '/movements',
          icon: TrendingUp,
          description: 'Inventory transactions'
        }
      ]
    },
    {
      title: 'Reports & Analytics',
      items: [
        {
          name: 'Reports',
          path: '/reports',
          icon: BarChart3,
          description: 'Inventory reports and analytics'
        },
        {
          name: 'Invoices',
          path: '/invoices',
          icon: FileText,
          description: 'Invoice management and tracking'
        }
      ]
    },
    {
      title: 'System',
      items: [
        {
          name: 'Settings',
          path: '/settings',
          icon: Settings,
          description: 'System configuration'
        }
      ]
    }
  ];


  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-screen bg-white shadow-xl border-r border-gray-200 z-50
        transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto lg:h-screen
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
        flex-shrink-0 flex flex-col sidebar-full-height
        overflow-hidden
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 flex items-center justify-center">
              <img 
                src="/logo.png" 
                alt="VTMS Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="text-lg font-bold text-gray-900">VTMS</h1>
                <p className="text-xs text-gray-500">Inventory System</p>
              </div>
            )}
          </div>
          
          {/* Toggle Buttons */}
          <div className="flex items-center space-x-2">
            {/* Desktop Collapse Toggle */}
            <button
              onClick={onCollapseToggle}
              className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-4 w-4 text-gray-600" />
            </button>
            
            {/* Mobile Close Button */}
            <button
              onClick={onToggle}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>


        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto min-h-0 h-full">
          {navigationSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {/* Section Title */}
              {!isCollapsed && (
                <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h3>
              )}
              
              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPath === item.path;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        onNavigate(item.path);
                        // Close mobile sidebar after navigation
                        if (window.innerWidth < 1024) {
                          onToggle();
                        }
                      }}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left
                        transition-all duration-200 group
                        ${isActive 
                          ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }
                        ${isCollapsed ? 'justify-center px-2' : ''}
                      `}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <Icon className={`
                        h-5 w-5 flex-shrink-0
                        ${isActive ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-700'}
                      `} />
                      
                      {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <p className="text-xs text-gray-500 truncate">{item.description}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

      </div>
    </>
  );
};

export default Sidebar;
